// Les salles en créneaux (D-130), logique pure : ce que montrait « Nos créneaux » (D-138 : la journée heure par heure, l'agenda d'un jour, une colonne par
// salle ; la liste gestionnaire › salle › créneau › équipe ; ce qui est à traiter), l'arbitrage des souhaits des
// vacances (où chaque souhait se pose, les chevauchements, les créneaux libres à proposer), les créneaux proposés à
// une séance sans salle ou à une séance habituelle pendant les vacances, la dérogation préparée, et les messages aux
// gestionnaires (vouvoiement : ils partent de la messagerie du responsable, jamais de l'app).
import { addDays, isoDay, parseDay, withArticle, clubOf, clubSubject, type HallKind } from './salles';
import { holidayOn, type HolidayKey } from './vacances';

// --- Ce que la base renvoie (hall_board, hall_period_view) -------------------------------------------------------------
export type ManagerKind = 'commune' | 'interco' | 'departement' | 'region' | 'association' | 'club' | 'autre';
export type AskHow = 'email' | 'form' | 'phone' | 'app';
export interface Manager {
  id: string; name: string; kind: ManagerKind; ask_how: AskHow; convention: string | null; position: number;
  contact_name?: string | null; contact_role?: string | null; contact_email?: string | null; contact_phone?: string | null;
  ask_url?: string | null; ask_cc?: string | null; ask_cc_email?: string | null; ask_note?: string | null;
}
export interface BHall {
  id: string; name: string; kind: HallKind; origin: 'ffbb' | 'club'; owner: 'club' | 'mairie'; manager_id: string | null;
  town: string | null; usage: string | null; capacity: number | null; inside_id: string | null; address: string | null;
}
export interface BTeam { id: string; name: string; category: string | null; gender: string; mine: boolean; coach: string | null; squads: { id: string; name: string }[] }
export interface BSlot { id: string; hall_id: string; season: string; weekday: number; starts: string; ends: string; team_id: string | null }
export interface Extra { id: string; hall_id: string; from_day: string; to_day: string; starts: string; ends: string; for_mode: 'touched' | 'free' | 'team'; team_id: string | null; loss_id: string | null; note: string | null }
export interface Loss { id: string; hall_ids: string[]; from_day: string; to_day: string; all_day: boolean; starts: string | null; ends: string | null; reason: string | null; hits: number; pending: number }
export type PeriodKey = HolidayKey | 'saison';
export type PeriodMode = 'same' | 'apart' | 'none';
export interface Period {
  id: string; season: string; key: PeriodKey; label: string; starts: string; back: string; mode: PeriodMode; status: 'open' | 'published';
  wishes_until: string | null; published_at: string | null; wishes?: number; open?: number; returned?: number; received?: number; expected?: number; cards_open?: number;
}
export interface Range { id: string; period_id?: string; hall_id: string; closed: boolean; weekdays: number[]; starts: string | null; ends: string | null; from_day: string | null; to_day: string | null; note: string | null }
export interface BOcc { event_id: string; team_id: string; kind: 'training' | 'match' | 'other'; title: string; hall_id: string; day: string; starts: string; ends: string; cancelled: boolean; one_off: boolean; squad_id: string | null; opponent: string | null }
export interface Ask { id: string; hall_id: string; slot_id: string | null; extra_id: string | null; team_id: string; weekly: boolean; day: string; starts: string; ends: string; message: string | null; status: string; who: string | null; mine: boolean; created_at: string }
export interface Hit { id: string; loss_id: string; event_id: string; day: string; team_id: string; kind: 'match' | 'training'; status: string }
export interface HolidayRow { season: string; key: HolidayKey; label: string; starts: string; back: string }
export interface Board {
  today: string; now: string; season: string; next_season: string; role: 'bureau' | 'resp' | 'coach' | 'tech'; manage: boolean; bureau: boolean; club: string;
  me: { name: string | null; title: string | null; club: string } | null;
  resp: { names: string[]; title: string } | null;
  zone: { zone: 'A' | 'B' | 'C' | null; department: string | null; confirmed_at: string | null; by: string | null; ffbb_code: string | null; address: string | null };
  holidays: HolidayRow[] | null;
  managers: Manager[]; halls: BHall[]; teams: BTeam[]; slots: BSlot[]; extras: Extra[]; losses: Loss[]; periods: Period[]; ranges: Range[];
  occ: BOcc[]; kept: { event_id: string; period_id: string }[]; asks: Ask[]; hits: Hit[];
  /** D-144 : un créneau a-t-il lieu ce jour-là ? (sa plage n'est valable que du… au…) ; sans elle, toujours. */
  slotOn?: (s: BSlot, day: string) => boolean;
}
export type WishStatus = 'sent' | 'accepted' | 'moved' | 'refused' | 'withdrawn';
export interface Wish {
  id: string; team_id: string; day: string; all_day: boolean; starts: string | null; ends: string | null; hall_id: string | null; message: string | null;
  status: WishStatus; f_hall: string | null; f_day: string | null; f_starts: string | null; f_ends: string | null; answer: string | null;
  event_id: string | null; returned: boolean; coach_note: string | null; created_at: string; by: string | null;
}
export interface PTeam { id: string; name: string; category: string | null; gender: string; mine: boolean; coach: string | null; expected: boolean; wishes: number; all_day: number }
export interface PView {
  manage: boolean; today: string; period: Period | null; ranges: Range[]; wishes: Wish[]; teams: PTeam[];
  cards: { event_id: string; team_id: string; choice: string }[]; reminded: string | null;
}

// --- Mots et heures --------------------------------------------------------------------------------------------------
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const WEEKDAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
export const WD3 = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const num = (d: number) => (d === 1 ? '1er' : String(d));
/** « 9h00 », « 18h30 » (heure « 09:00 »). */
export const h = (t: string | null | undefined) => (t ? `${Number(t.slice(0, 2))}h${t.slice(3, 5)}` : '');
/** « 9h00–17h00 » */
export const hr = (a: string | null | undefined, b: string | null | undefined) => `${h(a)}–${h(b)}`;
/** Dans l'agenda : « 16h–17h30 ». */
export const hShort = (t: string) => (t.slice(3, 5) === '00' ? `${Number(t.slice(0, 2))}h` : h(t));
/** « mercredi 21 octobre » */
export const longDate = (s: string) => `${WEEKDAYS[isoDay(s) - 1]} ${num(parseDay(s).getDate())} ${MONTHS[parseDay(s).getMonth()]}`;
/** « Mercredi 21 octobre » */
export const LongDate = (s: string) => cap(longDate(s));
/** « Mer. 21 » */
export const shortDate = (s: string) => `${WD3[isoDay(s) - 1]}. ${num(parseDay(s).getDate())}`;
/** « 14 novembre » */
export const dayMonth = (s: string) => `${num(parseDay(s).getDate())} ${MONTHS[parseDay(s).getMonth()]}`;
/** « Lun–ven », « Mer, sam », « Tous les jours » */
export function daysLine(w: number[]): string {
  const s = [...new Set(w)].sort((a, b) => a - b);
  if (s.length === 7) return 'Tous les jours';
  const run = s.length > 2 && s.every((d, i) => i === 0 || d === s[i - 1]! + 1);
  return run ? `${WD3[s[0]! - 1]}–${WD3[s[s.length - 1]! - 1]!.toLowerCase()}` : s.map((d, i) => (i ? WD3[d - 1]!.toLowerCase() : WD3[d - 1])).join(', ');
}
export const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
export const fromMin = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
/** « 2 h », « 1 h 30 », « 45 min » */
export const dur = (min: number) => (min < 60 ? `${min} min` : `${Math.floor(min / 60)} h${min % 60 ? ` ${String(min % 60).padStart(2, '0')}` : ''}`);
const overlap = (a0: number, a1: number, b0: number, b1: number) => a0 < b1 && b0 < a1;
/** « 18:00 » d'un instant, à l'heure de l'appareil (celle du club). */
export const clock = (iso: string) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

export const MANAGER_KIND: Record<ManagerKind, string> = {
  commune: 'Commune', interco: 'Intercommunalité', departement: 'Collège (Département)', region: 'Lycée (Région)', association: 'Association',
  club: 'Le club', autre: 'Autre',
};
export const ASK_HOW: Record<AskHow, string> = { email: 'par e-mail', form: 'par son formulaire', phone: 'par téléphone', app: 'Réservé dans l’app' };

// --- Les équipes : couleur, nom court ----------------------------------------------------------------------------------
/** Les couleurs des équipes : les seniors d'abord, puis des plus grands aux plus petits (comme les maquettes). */
export const TEAM_PALETTE = ['#0F4C81', '#BE185D', '#0E7490', '#C2410C', '#6D4BC2', '#15803D', '#3B82C4', '#A16207', '#475569', '#9D174D', '#0369A1', '#B91C1C'];
const rank = (t: { name: string; category?: string | null }) => {
  const s = `${t.category ?? ''} ${t.name}`;
  if (/senior/i.test(s)) return 0;
  const u = /U\s?(\d{1,2})/i.exec(s);
  return u ? 100 - Number(u[1]) : 200;
};
export function teamColors(teams: { id: string; name: string; category?: string | null }[]): Map<string, string> {
  const sorted = [...teams].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'fr'));
  return new Map(sorted.map((t, i) => [t.id, TEAM_PALETTE[i % TEAM_PALETTE.length]!]));
}
/** « SF », « U13 F », « U15 G », « U11-1 », « U11 M ». */
export function teamShort(name: string): string {
  const n = name.trim();
  const sen = /^seniors?\s+(filles?|f[ée]minines?|gar[cç]ons?|masculins?|mixtes?)\b/i.exec(n);
  if (sen) return `S${/^f/i.test(sen[1]!) ? 'F' : /^m(ix)/i.test(sen[1]!) ? 'M' : 'G'}`;
  const u = /^(U\s?\d{1,2})\s+(filles?|gar[cç]ons?|mixtes?)?\s*(\d)?\s*$/i.exec(n);
  if (u) {
    const cat = u[1]!.replace(/\s/, '').toUpperCase();
    if (u[3]) return `${cat}-${u[3]}`;
    return u[2] ? `${cat} ${/^f/i.test(u[2]) ? 'F' : /^g/i.test(u[2]) ? 'G' : 'M'}` : cat;
  }
  return n.length > 8 ? n.split(/\s+/).map(w => w.charAt(0).toUpperCase()).join('') : n;
}

// --- Les salles : en-tête de colonne, ordre ---------------------------------------------------------------------------
/** « Gymnase de Rivebelle » → Rivebelle / gymnase ; « Salle Bellevue » → Bellevue / salle ; « Dojo municipal »
 *  → Dojo / municipal ; « Gymnase du collège » → Collège / gymnase. */
export function hallHead(name: string): { main: string; sub: string } {
  const m = /^(gymnase|salle|halle|complexe sportif|complexe|dojo|palais des sports|espace|stade|maison des sports)\s+(.+)$/i.exec(name.trim());
  if (!m) return { main: name.trim(), sub: '' };
  const type = m[1]!.toLowerCase(), rest = m[2]!.trim();
  const art = /^(de la|de l’|de l'|du|des|de|d’|d')\s*/i.exec(rest);
  if (art) return { main: cap(rest.slice(art[0].length)), sub: type };
  if (/^[a-zà-ÿ]/.test(rest)) return { main: cap(type), sub: rest };
  return { main: rest, sub: type };
}
/** Les salles de sport, dans l'ordre des gestionnaires (le club en dernier), puis par nom. */
export function sportHalls(b: Pick<Board, 'halls' | 'managers'>): BHall[] {
  const pos = new Map(b.managers.map((m, i) => [m.id, m.kind === 'club' ? 1000 + i : i]));
  return b.halls.filter(x => x.kind !== 'reception')
    .sort((a, c) => (a.kind === 'autre' ? 1 : 0) - (c.kind === 'autre' ? 1 : 0) || (pos.get(a.manager_id ?? '') ?? 999) - (pos.get(c.manager_id ?? '') ?? 999)
      || a.name.localeCompare(c.name, 'fr'));
}

// --- La période d'un jour -----------------------------------------------------------------------------------------
export type DayMode = 'school' | PeriodMode;
export interface DayPeriod { mode: DayMode; holiday: HolidayRow | null; period: Period | null }
export function periodOfDay(b: Pick<Board, 'holidays' | 'periods'>, day: string): DayPeriod {
  const hol = holidayOn(b.holidays ?? [], day);
  if (!hol) return { mode: 'school', holiday: null, period: null };
  const p = b.periods.find(x => x.key === hol.key && x.season === hol.season) ?? null;
  return { mode: p?.mode ?? 'same', holiday: hol, period: p };
}
/** La saison (« 2026-2027 ») d'un jour : du 1er juillet au 30 juin. */
export const seasonOf = (day: string) => { const y = Number(day.slice(0, 4)), m = Number(day.slice(5, 7)); return m >= 7 ? `${y}-${y + 1}` : `${y - 1}-${y}`; };

/** Une plage ouverte d'une salle ce jour-là (la première). */
export function openOn(ranges: Range[], hallId: string, day: string): { starts: string; ends: string } | null {
  const r = ranges.filter(x => x.hall_id === hallId && !x.closed && x.starts && x.ends && x.weekdays.includes(isoDay(day))
    && (!x.from_day || day >= x.from_day) && (!x.to_day || day <= x.to_day)).sort((a, c) => a.starts!.localeCompare(c.starts!))[0];
  return r ? { starts: r.starts!.slice(0, 5), ends: r.ends!.slice(0, 5) } : null;
}
/** Le créneau perdu qui prend cette salle ce jour-là, sur ces heures. */
export function lostAt(losses: Loss[], hallId: string, day: string, a?: string, z?: string): Loss | null {
  return losses.find(l => l.hall_ids.includes(hallId) && day >= l.from_day && day <= l.to_day
    && (l.all_day || !a || !z || overlap(toMin(a), toMin(z), toMin(l.starts!), toMin(l.ends!)))) ?? null;
}

// --- L'agenda d'un jour (s1, s1c) ---------------------------------------------------------------------------------
export type BlockKind = 'team' | 'free' | 'match' | 'lost' | 'extra' | 'range' | 'session';
export interface AgBlock {
  col: string; start: string; end: string; kind: BlockKind; label: string; sub: string; color?: string; mine?: boolean;
  asked?: number; slotId?: string; extraId?: string; eventId?: string; teamId?: string | null; lossId?: string; state?: 'kept' | 'pending';
}
export interface AgDay { day: string; dp: DayPeriod; cols: { id: string; main: string; sub: string }[]; from: number; to: number; blocks: AgBlock[] }

export function agendaDay(b: Board, day: string): AgDay {
  const dp = periodOfDay(b, day);
  const wd = isoDay(day);
  const colors = teamColors(b.teams);
  const team = (id: string | null) => (id ? b.teams.find(t => t.id === id) ?? null : null);
  const season = seasonOf(day);
  const ranges = dp.period ? b.ranges.filter(r => r.period_id === dp.period!.id) : [];
  const halls = sportHalls(b).filter(x => b.slots.some(s => s.hall_id === x.id) || ranges.some(r => r.hall_id === x.id)
    || b.extras.some(e => e.hall_id === x.id) || b.occ.some(o => o.hall_id === x.id && o.kind !== 'match'));
  const blocks: AgBlock[] = [];
  const kept = new Set(b.kept.filter(k => !dp.period || k.period_id === dp.period.id).map(k => k.event_id));
  const occ = b.occ.filter(o => o.day === day && !o.cancelled && halls.some(x => x.id === o.hall_id));
  const slotWeek = dp.mode === 'school' || dp.mode === 'same';
  if (slotWeek) {
    for (const s of b.slots.filter(x => x.season === season && x.weekday === wd && halls.some(y => y.id === x.hall_id) && (!b.slotOn || b.slotOn(x, day)))) {
      const a = s.starts.slice(0, 5), z = s.ends.slice(0, 5);
      const lost = lostAt(b.losses, s.hall_id, day, a, z);
      if (lost) { blocks.push({ col: s.hall_id, start: a, end: z, kind: 'lost', label: 'Perdu', sub: lost.reason ?? 'créneau perdu', slotId: s.id, lossId: lost.id, teamId: s.team_id }); continue; }
      if (s.team_id) {
        const t = team(s.team_id);
        blocks.push({ col: s.hall_id, start: a, end: z, kind: 'team', label: t ? teamShort(t.name) : 'Équipe', sub: `${hShort(a)}–${hShort(z)}`,
          color: colors.get(s.team_id), mine: !!t?.mine, slotId: s.id, teamId: s.team_id });
      } else {
        const asked = b.asks.filter(x => x.slot_id === s.id && x.status === 'pending').length;
        blocks.push({ col: s.hall_id, start: a, end: z, kind: 'free', label: 'Libre', sub: `${hShort(a)}–${hShort(z)}`, asked, slotId: s.id, teamId: null });
      }
    }
  } else if (dp.mode === 'apart') {
    for (const x of halls) {
      const o = openOn(ranges, x.id, day);
      if (o) blocks.push({ col: x.id, start: o.starts, end: o.ends, kind: 'range', label: 'Ouvert', sub: `${hShort(o.starts)}–${hShort(o.ends)}` });
    }
  }
  for (const e of b.extras.filter(x => day >= x.from_day && day <= x.to_day && halls.some(y => y.id === x.hall_id))) {
    const a = e.starts.slice(0, 5), z = e.ends.slice(0, 5);
    const t = team(e.team_id);
    blocks.push({ col: e.hall_id, start: a, end: z, kind: 'extra', label: e.for_mode === 'team' && t ? teamShort(t.name) : e.for_mode === 'free' ? 'Libre' : 'En plus',
      sub: e.for_mode === 'free' ? `en plus · ${hShort(a)}–${hShort(z)}` : `${hShort(a)}–${hShort(z)}`, color: e.for_mode === 'team' ? colors.get(e.team_id!) : undefined,
      mine: !!t?.mine, extraId: e.id, teamId: e.team_id, asked: b.asks.filter(k => k.extra_id === e.id && k.status === 'pending').length });
  }
  for (const o of occ) {
    const a = clock(o.starts), z = clock(o.ends);
    if (o.kind === 'match') {
      const t = team(o.team_id);
      const sq = t?.squads.find(q => q.id === o.squad_id)?.name;
      blocks.push({ col: o.hall_id, start: a, end: z, kind: 'match', label: `${sq ?? (t ? teamShort(t.name) : 'Match')} – ${o.opponent ?? o.title}`, sub: `match · ${hShort(a)}`,
        color: colors.get(o.team_id), eventId: o.event_id, teamId: o.team_id, mine: !!t?.mine });
      continue;
    }
    // Une séance que le créneau de son équipe montre déjà : rien de plus.
    if (slotWeek && blocks.some(x => x.col === o.hall_id && (x.kind === 'team' || x.kind === 'extra') && x.teamId === o.team_id && overlap(toMin(a), toMin(z), toMin(x.start), toMin(x.end)))) continue;
    if (slotWeek && !o.one_off) continue;   // une série hors de ses créneaux : le planning de la salle la montre
    const t = team(o.team_id);
    const state = !o.one_off && dp.mode !== 'school' ? (kept.has(o.event_id) ? 'kept' : 'pending') : undefined;
    blocks.push({ col: o.hall_id, start: a, end: z, kind: 'session', label: t ? teamShort(t.name) : 'Séance',
      sub: state === 'kept' ? 'hors planning' : state === 'pending' ? 'à décider' : `${hShort(a)}–${hShort(z)}`,
      color: colors.get(o.team_id), mine: !!t?.mine, eventId: o.event_id, teamId: o.team_id, state });
  }
  const mins = blocks.flatMap(x => [toMin(x.start), toMin(x.end)]);
  let from = mins.length ? Math.floor(Math.min(...mins) / 60) : 17, to = mins.length ? Math.ceil(Math.max(...mins) / 60) : 21;
  if (to - from < 4) to = Math.min(24, from + 4);
  if (to - from < 4) from = to - 4;
  return { day, dp, cols: halls.map(x => ({ id: x.id, ...hallHead(x.name) })), from, to, blocks };
}

/** Le point de chaque jour de la bande : le responsable, une demande ou un créneau perdu ; un coach, ses créneaux. */
export function dayDot(b: Board, day: string): string | null {
  const ag = agendaDay(b, day);
  if (b.manage) {
    if (ag.blocks.some(x => x.kind === 'lost')) return 'var(--ko)';
    if (ag.blocks.some(x => (x.asked ?? 0) > 0)) return 'var(--accent)';
    return null;
  }
  const mine = ag.blocks.find(x => x.mine && (x.kind === 'team' || x.kind === 'session' || x.kind === 'extra'));
  return mine?.color ?? null;
}

// --- La liste (s1-liste) ------------------------------------------------------------------------------------------
export interface ListHall { hall: BHall; slots: BSlot[]; free: number; asks: number }
export interface ListGroup { manager: Manager | null; halls: ListHall[]; count: number }
export function listGroups(b: Board, season = b.season): ListGroup[] {
  const halls = sportHalls(b);
  const groups: ListGroup[] = [];
  const of = (m: Manager | null) => halls.filter(x => (m ? x.manager_id === m.id : !b.managers.some(y => y.id === x.manager_id)));
  for (const m of [...b.managers.filter(x => x.kind !== 'club'), ...b.managers.filter(x => x.kind === 'club'), null]) {
    const hs = of(m).map(x => {
      const slots = b.slots.filter(s => s.hall_id === x.id && s.season === season).sort((a, c) => a.weekday - c.weekday || a.starts.localeCompare(c.starts));
      return { hall: x, slots, free: slots.filter(s => !s.team_id).length, asks: b.asks.filter(a => a.hall_id === x.id && a.status === 'pending').length };
    }).filter(x => x.slots.length);
    if (hs.length) groups.push({ manager: m, halls: hs, count: hs.reduce((n, x) => n + x.slots.length, 0) });
  }
  return groups;
}

// --- À traiter (le responsable, le bureau) --------------------------------------------------------------------------
export interface Todo { key: string; title: string; sub: string; href?: string; ask?: string; /** « les souhaits de la Toussaint », « une demande d’Inès » */ short: string }
/** « d’Inès », « de Marc » */
export const deWho = (who: string | null) => (who && /^(?:[aeiouàâéèêëîïôöûü]|y(?=[^aeiouy]))/i.test(who) ? `d’${who}` : `de ${who ?? 'quelqu’un'}`);
/** « de la Toussaint », « de Noël », « d’hiver », « de la saison 2027-2028 » */
export const ofLabel = (label: string) => label.replace(/^Vacances /, '').replace(/^Planning /, '');
export function todos(b: Board, extra: Todo[] = []): Todo[] {
  if (!b.manage) return [];
  const out: Todo[] = [];
  const team = (id: string) => b.teams.find(t => t.id === id);
  const hall = (id: string) => b.halls.find(x => x.id === id)?.name ?? 'Salle';
  for (const p of b.periods.filter(x => x.back > b.today && (x.mode === 'apart' || x.key === 'saison'))) {
    if (p.status === 'open') {
      out.push({ key: `p:${p.id}`, title: p.label, short: `les souhaits ${ofLabel(p.label)}`, href: p.key === 'saison' ? '#/salles/saison' : `#/salles/vacances/${p.key}/${p.season}`,
        sub: `Souhaits des coachs : ${p.received ?? 0} reçus sur ${p.expected ?? 0}${p.wishes_until ? ` · jusqu’au ${longDate(p.wishes_until)}` : ''}` });
    } else if ((p.open ?? 0) > 0) {
      out.push({ key: `p:${p.id}`, title: p.label, short: `des souhaits ${ofLabel(p.label)} à revoir`, href: `#/salles/arbitrer/${p.id}`, sub: `${p.open} souhait${(p.open ?? 0) > 1 ? 's' : ''} à revoir${p.returned ? ' (refusé par le coach)' : ''}` });
    }
  }
  for (const a of b.asks.filter(x => x.status === 'pending')) {
    const t = team(a.team_id);
    out.push({ key: `a:${a.id}`, ask: a.id, title: `${a.who ?? 'Un coach'} demande le créneau libre`, short: `une demande ${deWho(a.who)}`,
      sub: `${hall(a.hall_id)} · ${a.weekly ? WEEKDAYS[isoDay(a.day) - 1] : longDate(a.day)} ${hr(a.starts, a.ends)}${t ? ` · ${t.name}` : ''}` });
  }
  for (const l of b.losses.filter(x => x.pending > 0 && x.to_day >= b.today)) {
    out.push({ key: `l:${l.id}`, title: `Créneau perdu${l.reason ? ` · ${l.reason}` : ''}`, short: 'un créneau perdu', href: `#/salles/perdu/${l.id}`,
      sub: `${l.pending} ${l.pending > 1 ? 'conséquences' : 'conséquence'} en attente · ${l.from_day === l.to_day ? longDate(l.from_day) : `du ${longDate(l.from_day)} au ${longDate(l.to_day)}`}` });
  }
  return [...out, ...extra];
}

// --- Les souhaits, l'arbitrage (s4, s5) -----------------------------------------------------------------------------
/** Où un souhait se pose : décidé (accepté, déplacé) là où le responsable l'a mis ; sinon la salle souhaitée (toute la
 *  journée : sa plage ; sans salle : la première ouverte qui le contient). */
export interface Placed { wish: Wish; hall: string | null; day: string; start: string; end: string; decided: boolean }
export function placeWish(w: Wish, ranges: Range[], halls: { id: string }[]): Placed {
  if ((w.status === 'accepted' || w.status === 'moved') && w.f_hall && w.f_day && w.f_starts && w.f_ends) {
    return { wish: w, hall: w.f_hall, day: w.f_day, start: w.f_starts.slice(0, 5), end: w.f_ends.slice(0, 5), decided: true };
  }
  let hall = w.hall_id;
  if (w.all_day) {
    let o: { starts: string; ends: string } | null = hall ? openOn(ranges, hall, w.day) : null;
    if (!hall) for (const x of halls) { const oo = openOn(ranges, x.id, w.day); if (oo) { hall = x.id; o = oo; break; } }
    return { wish: w, hall, day: w.day, start: o?.starts ?? '09:00', end: o?.ends ?? '17:00', decided: false };
  }
  return { wish: w, hall, day: w.day, start: w.starts!.slice(0, 5), end: w.ends!.slice(0, 5), decided: false };
}
export interface Clash { a: string; b: string; minutes: number }
/** Les chevauchements d'un jour, salle par salle (souhaits en attente ou décidés ; les refusés ne comptent pas). */
export function clashes(placed: Placed[]): Clash[] {
  const live = placed.filter(p => p.hall && p.wish.status !== 'refused' && p.wish.status !== 'withdrawn');
  const out: Clash[] = [];
  for (let i = 0; i < live.length; i++) for (let j = i + 1; j < live.length; j++) {
    const a = live[i]!, c = live[j]!;
    if (a.hall !== c.hall || a.day !== c.day) continue;
    const s = Math.max(toMin(a.start), toMin(c.start)), e = Math.min(toMin(a.end), toMin(c.end));
    if (s < e && (!a.decided || !c.decided)) out.push({ a: a.wish.id, b: c.wish.id, minutes: e - s });
  }
  return out;
}
export interface ArbSummary { accepted: number; clashes: number; toSee: number; open: number; total: number }
export function arbSummary(v: PView, halls: { id: string }[]): ArbSummary {
  const live = v.wishes.filter(w => w.status !== 'withdrawn');
  const placed = live.map(w => placeWish(w, v.ranges, halls));
  const cl = clashes(placed);
  const inClash = new Set(cl.flatMap(c => [c.a, c.b]));
  const open = live.filter(w => w.status === 'sent');
  return { accepted: live.filter(w => w.status === 'accepted' || w.status === 'moved').length, clashes: cl.length,
    toSee: open.filter(w => !inClash.has(w.id)).length, open: open.length, total: live.length };
}
/** Les jours à arbitrer : ceux qui ont une plage ouverte ou un souhait, semaine par semaine (lundi). */
export function arbDays(v: PView): string[] {
  if (!v.period) return [];
  const out: string[] = [];
  for (let d = v.period.starts; d < v.period.back; d = addDays(d, 1)) {
    const has = v.wishes.some(w => w.status !== 'withdrawn' && (w.day === d || w.f_day === d))
      || v.ranges.some(r => !r.closed && r.weekdays.includes(isoDay(d)) && (!r.from_day || d >= r.from_day) && (!r.to_day || d <= r.to_day));
    if (has) out.push(d);
  }
  return out;
}
export const mondayOf = (d: string) => addDays(d, 1 - isoDay(d));

/** Le premier trou libre d'au moins `len` minutes dans [a, z], hors de `busy` ; d'abord à `prefer` s'il est libre. */
export function freeAt(a: string, z: string, busy: [number, number][], len: number, prefer?: string): string | null {
  const lo = toMin(a), hi = toMin(z);
  const fits = (s: number) => s >= lo && s + len <= hi && !busy.some(([b0, b1]) => overlap(s, s + len, b0, b1));
  if (prefer && fits(toMin(prefer))) return prefer;
  for (let s = lo; s + len <= hi; s += 15) if (fits(s)) return fromMin(s);
  return null;
}
/** Les trous libres d'une plage (pour la frise : « libre »). */
export function gaps(a: string, z: string, busy: [number, number][], min = 30): [string, string][] {
  const out: [string, string][] = [];
  let cur = toMin(a);
  for (const [b0, b1] of [...busy].sort((x, y) => x[0] - y[0])) {
    if (b1 <= cur) continue;
    if (b0 - cur >= min) out.push([fromMin(cur), fromMin(Math.min(b0, toMin(z)))]);
    cur = Math.max(cur, b1);
  }
  if (toMin(z) - cur >= min) out.push([fromMin(cur), z]);
  return out;
}
export interface Proposal { hall: string; day: string; start: string; end: string; why: string }
/** Déplacer un souhait (s5b) : même jour, autre salle ; même heure, autre jour ; puis « autre chose ». */
export function moveProposals(v: PView, w: Wish, halls: { id: string; name: string }[]): Proposal[] {
  const p = placeWish(w, v.ranges, halls);
  const len = toMin(p.end) - toMin(p.start);
  const decided = v.wishes.filter(x => x.id !== w.id && (x.status === 'accepted' || x.status === 'moved')).map(x => placeWish(x, v.ranges, halls));
  const busy = (hall: string, day: string) => decided.filter(x => x.hall === hall && x.day === day).map(x => [toMin(x.start), toMin(x.end)] as [number, number]);
  const out: Proposal[] = [];
  for (const x of halls.filter(y => y.id !== p.hall)) {
    const o = openOn(v.ranges, x.id, p.day);
    const s = o ? freeAt(o.starts, o.ends, busy(x.id, p.day), len, p.start) : null;
    if (s) { out.push({ hall: x.id, day: p.day, start: s, end: fromMin(toMin(s) + len), why: 'Même jour, autre salle · libre' }); break; }
  }
  if (p.hall && v.period) {
    for (let d = addDays(p.day, 1); d < v.period.back && out.length < 2; d = addDays(d, 1)) {
      const o = openOn(v.ranges, p.hall, d);
      if (!o) continue;
      const s = freeAt(o.starts, o.ends, busy(p.hall, d), len, p.start);
      if (s === p.start) out.push({ hall: p.hall, day: d, start: s, end: fromMin(toMin(s) + len), why: 'Même heure, autre jour · libre' });
    }
  }
  return out;
}

/** Une séance sans salle (s8) : d'abord les créneaux en plus de ce jour (même heure si possible), puis les créneaux
 *  libres du club ce jour-là, hors des salles perdues. */
export function hitProposals(b: Board, hit: { day: string; starts: string; ends: string; loss_id: string }): (Proposal & { label: string })[] {
  const len = toMin(hit.ends) - toMin(hit.starts);
  const lost = b.losses.find(l => l.id === hit.loss_id);
  const busy = (hall: string) => b.occ.filter(o => o.hall_id === hall && o.day === hit.day && !o.cancelled).map(o => [toMin(clock(o.starts)), toMin(clock(o.ends))] as [number, number]);
  const name = (id: string) => b.halls.find(x => x.id === id)?.name ?? 'Salle';
  const out: (Proposal & { label: string })[] = [];
  for (const e of b.extras.filter(x => hit.day >= x.from_day && hit.day <= x.to_day && (x.for_mode === 'free' || (x.for_mode === 'touched' && x.loss_id === hit.loss_id)))) {
    const bz = busy(e.hall_id);
    const same = freeAt(e.starts, e.ends, bz, len, hit.starts);
    if (same === hit.starts) out.push({ hall: e.hall_id, day: hit.day, start: same, end: fromMin(toMin(same) + len), label: name(e.hall_id),
      why: `Même heure · ${e.note ?? (lost?.reason ? `prêté pour ${lost.reason.toLowerCase()}` : 'créneau en plus')}` });
    const later = gaps(e.starts, e.ends, [...bz, [toMin(hit.starts), toMin(hit.ends)]], len)
      .map(g => { const s0 = Math.max(toMin(g[0]), 13 * 60); return s0 + len <= toMin(g[1]) ? fromMin(s0) : null; }).find((s): s is string => !!s && s !== hit.starts);
    if (later) out.push({ hall: e.hall_id, day: hit.day, start: later, end: fromMin(toMin(later) + len), label: name(e.hall_id), why: `${toMin(later) >= 12 * 60 ? 'L’après-midi' : 'Le matin'} · libre` });
  }
  for (const s of b.slots.filter(x => !x.team_id && x.season === seasonOf(hit.day) && x.weekday === isoDay(hit.day) && !(lost?.hall_ids.includes(x.hall_id)))) {
    const st = freeAt(s.starts.slice(0, 5), s.ends.slice(0, 5), busy(s.hall_id), Math.min(len, toMin(s.ends) - toMin(s.starts)));
    if (st) out.push({ hall: s.hall_id, day: hit.day, start: st, end: fromMin(Math.min(toMin(st) + len, toMin(s.ends))), label: name(s.hall_id), why: 'Créneau libre du club' });
  }
  return out.slice(0, 3);
}

/** Une séance habituelle pendant les vacances (s6) : pour chaque jour où elle tombe, un créneau des vacances (plage
 *  ouverte, sans séance publiée), à son heure si possible. Les jours où l'équipe a déjà sa séance des vacances : pause. */
export function vacationMoves(b: Board, ev: { event_id: string; team_id: string; hall_id: string | null; at: string; until: string; days: string[]; replaced: string[] | null; period_id: string }): { day: string; pick: Proposal | null; replaced: boolean }[] {
  const ranges = b.ranges.filter(r => r.period_id === ev.period_id);
  const len = toMin(ev.until) - toMin(ev.at);
  const halls = sportHalls(b);
  return ev.days.map(day => {
    if ((ev.replaced ?? []).includes(day)) return { day, pick: null, replaced: true };
    const busy = (hall: string) => b.occ.filter(o => o.hall_id === hall && o.day === day && !o.cancelled && o.event_id !== ev.event_id).map(o => [toMin(clock(o.starts)), toMin(clock(o.ends))] as [number, number]);
    for (const x of [...halls.filter(y => y.id === ev.hall_id), ...halls.filter(y => y.id !== ev.hall_id)]) {
      const o = openOn(ranges, x.id, day);
      const s = o ? freeAt(o.starts, o.ends, busy(x.id), len, ev.at) : null;
      if (s) return { day, pick: { hall: x.id, day, start: s, end: fromMin(toMin(s) + len), why: x.id === ev.hall_id ? 'même salle' : 'autre salle' }, replaced: false };
    }
    return { day, pick: null, replaced: false };
  });
}

/** La dérogation préparée (s7b) : trois créneaux du club, le même jour de la semaine et à la même heure, les semaines
 *  suivantes, dans la même salle, sans match à domicile ni créneau perdu. */
export function derogSlots(b: Board, m: { day: string; starts: string; hall_id: string | null; event_id: string }): { date: string; time: string; location: string }[] {
  const hall = b.halls.find(x => x.id === m.hall_id);
  const out: { date: string; time: string; location: string }[] = [];
  for (let k = 1; k <= 8 && out.length < 3; k++) {
    const d = addDays(m.day, 7 * k);
    if (m.hall_id && lostAt(b.losses, m.hall_id, d)) continue;
    if (b.occ.some(o => o.day === d && o.kind === 'match' && o.hall_id === m.hall_id && !o.cancelled && o.event_id !== m.event_id
      && Math.abs(toMin(clock(o.starts)) - toMin(m.starts)) < 150)) continue;
    out.push({ date: d, time: m.starts, location: hall?.name ?? '' });
  }
  return out;
}

// --- Les messages aux gestionnaires (vouvoiement) ------------------------------------------------------------------
export interface MgrMail { to: string; cc: string; subject: string; body: string; mailto: string; email: string }
/** « Madame, », « Monsieur, », sinon « Madame, Monsieur, ». */
const salute = (n: string | null | undefined) => (/^(mme|madame)\b/i.test(n ?? '') ? 'Madame,' : /^(m\.|monsieur)\s/i.test(n ?? '') ? 'Monsieur,' : 'Madame, Monsieur,');
export function managerMail(o: { manager: Manager; subject: string; wish: string; question: string; club: string; signer: { name: string | null; title: string | null } }): MgrMail {
  const m = o.manager;
  const to = [m.contact_name, m.contact_role].filter(Boolean).join(', ') || m.name;
  const toLine = `${to}${m.contact_name ? '' : m.contact_email ? ` (${m.contact_email})` : ''}`;
  const cc = [m.ask_cc, m.ask_cc_email && !m.ask_cc ? m.ask_cc_email : null].filter(Boolean).join(' ');
  const sign = o.signer.name ? `${o.signer.name}, ${o.signer.title?.trim() || 'pour le bureau'} ${clubOf(o.club)}` : `Le bureau ${clubOf(o.club)}`;
  const body = [salute(m.contact_name), '', `${clubSubject(o.club)} souhaiterait ${o.wish}`, '', o.question, '', 'Bien cordialement,', sign].join('\n');
  const email = m.contact_email?.trim() ?? '';
  const q = [m.ask_cc_email ? `cc=${encodeURIComponent(m.ask_cc_email)}` : '', `subject=${encodeURIComponent(o.subject)}`, `body=${encodeURIComponent(body)}`].filter(Boolean).join('&');
  return { to: toLine, cc, subject: o.subject, body, mailto: `mailto:${encodeURIComponent(email)}?${q}`, email };
}
/** « du lundi 19 au vendredi 30 octobre » : les jours de semaine d'une période (ou ses bornes). */
export function workdaysSpan(starts: string, back: string): string {
  let a = starts; while (isoDay(a) > 5 && a < back) a = addDays(a, 1);
  let z = addDays(back, -1); while (isoDay(z) > 5 && z > a) z = addDays(z, -1);
  const sameMonth = a.slice(0, 7) === z.slice(0, 7);
  return sameMonth ? `du ${WEEKDAYS[isoDay(a) - 1]} ${num(parseDay(a).getDate())} au ${longDate(z)}` : `du ${longDate(a)} au ${longDate(z)}`;
}
/** Demander une salle pendant des vacances (s3b). */
export const vacationAsk = (hallName: string, label: string, starts: string, back: string, open: { starts: string; ends: string } | null) => ({
  subject: `${cap(hallName)} pendant les ${label.replace(/^Vacances /, 'vacances ')}`,
  wish: `utiliser ${withArticle(hallName)} pendant les ${label.replace(/^Vacances /, 'vacances ')}, ${workdaysSpan(starts, back)}${open ? `, de ${h(open.starts)} à ${h(open.ends)}` : ', en journée'}, pour les séances de ses équipes.`,
  question: 'Pouvez-vous nous dire quelles journées sont possibles ?',
});
/** Demander un créneau en plus. */
export const extraAsk = (hallName: string, day: string, a: string, z: string, why: string | null) => ({
  subject: `${cap(hallName)} le ${longDate(day)}`,
  wish: `utiliser ${withArticle(hallName)} le ${longDate(day)}, de ${h(a)} à ${h(z)}${why ? `, ${why}` : ''}.`,
  question: 'Pouvez-vous nous dire si c’est possible ?',
});
/** Demander les créneaux de la saison suivante. */
export const seasonAsk = (hallNames: string[], season: string) => ({
  subject: `Créneaux de la saison ${season}`,
  wish: `connaître les créneaux que vous pourrez lui attribuer pour la saison ${season}${hallNames.length ? ` (${hallNames.map(n => withArticle(n)).join(', ')})` : ''}, comme cette saison.`,
  question: 'Pouvez-vous nous les indiquer, et nous dire si une convention est à signer ?',
});
